import React, { useState } from "react";
import { Controller, UseFormClearErrors } from "react-hook-form";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { inputStyles } from "../mui-styles";
import { VisibilityIcon, VisibilityOffIcon } from "../muiIconsImport";
import { Box, ButtonBase, FormHelperText } from "@mui/material";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import PhoneIcon from "@mui/icons-material/Phone";

interface InputProps {
  label: string;
  id: string;
  type?: string;
  control: any;
  errorMessage?: string | undefined;
  placeholder?: string;
  defaultValue?: string | number | undefined;
  disabled?: boolean;
  prefix?: boolean;
  prefixText?: string;
  required?: boolean;
  customErrorMessage?: string;
  applyNumberValidation?: boolean;
  maxLength?: number;
}

export const InputForm: React.FC<InputProps> = ({
  label,
  id,
  type = "text",
  control,
  errorMessage,
  placeholder = `Enter the ${label}`,
  defaultValue,
  disabled = false,
  prefix = false,
  prefixText,
  required = true,
  customErrorMessage = `${label} is required`,
  applyNumberValidation = false, // Default value is false
  maxLength = 200,
}) => {
  const patternRule = applyNumberValidation
    ? {
      pattern: {
        value: /^\d+$/,
        message: "Please enter a valid number.",
      },
    }
    : {};

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      ((e.target as HTMLInputElement).value.length >= maxLength ||
        e.key === "e" ||
        e.key === "E") &&
      type === "number"
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue || ""}
        rules={{
          required: required ? customErrorMessage : false,
          ...patternRule,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            sx={inputStyles.select}
            fullWidth
            id={id}
            label={label}
            {...field}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            type={type}
            placeholder={placeholder}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            error={Boolean(error?.message) || Boolean(errorMessage)}
            helperText={error?.message}
            variant="outlined"
            className="my-2"
            autoComplete="off"
            InputProps={{
              startAdornment: prefix && (
                <InputAdornment position="start">{prefixText}</InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: maxLength,
            }}
          />
        )}
      />
    </>
  );
};

export const PhoneNumInputForm: React.FC<InputProps> = ({
  label,
  id,
  type = "text",
  control,
  errorMessage,
  placeholder = `Enter the ${label}`,
  defaultValue,
  disabled = false,
  prefix = true,
  prefixText = "+91 |",
  required = true,
  customErrorMessage = `${label} is required`,
}) => {
  return (
    <>
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue || ""}
        rules={{
          required: required ? customErrorMessage : false,
          // pattern: {
          //   value: /^\d{10}$/,
          //   message: "Please enter a valid 10-digit mobile number",
          // },
          pattern: {
            value: /^(?!0\d)\d+(\.\d+)?$/,
            message: "Amount cannot start with 0.",
          }
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            sx={inputStyles.select}
            fullWidth
            id={id}
            label={label}
            {...field}
            value={field.value}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              const newValue = numericValue.slice(0, 10);
              field.onChange(newValue);
            }}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error?.message) || Boolean(errorMessage)}
            helperText={error?.message}
            variant="outlined"
            className="my-2"
            autoComplete="off"
            InputProps={{
              startAdornment: prefix && (
                <InputAdornment position="start">{prefixText}</InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "numeric",
            }}
          />
        )}
      />
    </>
  );
};

interface ExtendedInputProps extends InputProps {
  clearErrors?: UseFormClearErrors<any>;
  setValue?: any; // You can replace 'any' with the specific type of your form values if you have it defined
}

export const PhoneNumLanInputForm: React.FC<ExtendedInputProps> = ({
  label,
  id,
  control,
  errorMessage,
  defaultValue,
  disabled = false,
  required = true,
  customErrorMessage = `${label} is required`,
  clearErrors,
  setValue,
}) => {
  // Determine if initial mode is landline by checking for a hyphen separator
  const [isLandline, setIsLandline] = useState(() => {
    if (typeof defaultValue === "string" && defaultValue.includes("-")) {
      return true;
    }
    return false;
  });

  const handleToggle = (mode: boolean) => {
    if (isLandline !== mode) {
      setIsLandline(mode);
      clearErrors?.(id);
      setValue(id, ""); // Clear the field value when toggling modes
    }
  };

  /* --- UPDATED VALIDATION RULES --- */
  const validateLandline = (val: string) => {
    if (!required && !val) return true;
    if (required && !val) return customErrorMessage;

    const [std, number] = val.split("-");
    if (!std || !number) return "Both STD code and landline number are required";

    if (std.length < 2 || std.length > 8) {
      return "STD code must be between 2 and 8 digits";
    }

    if (number.length < 5 || number.length > 11) {
      return "Landline number must be between 5 and 11 digits";
    }

    return true;
  };

  const validateMobile = (val: string) => {
    if (!required && !val) return true;
    if (required && !val) return customErrorMessage;
    if (!/^[6-9]\d{9}$/.test(val)) {
      return "Please enter a valid 10-digit mobile number";
    }
    return true;
  };

  return (
    <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start", width: "100%" }} className="my-2">
      {/* Mode Switcher */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "#F4F6F8",
          borderRadius: "20px",
          padding: "3px",
          border: "1px solid #E4E7EC",
          position: "relative",
          height: "40px",
          mt: "8px",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "3px",
            bottom: "3px",
            left: isLandline ? "calc(50% + 1px)" : "3px",
            width: "calc(50% - 4px)",
            backgroundColor: "#E6F0FA",
            border: "1px solid #A4C6F4",
            borderRadius: "16px",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0px 2px 4px rgba(144, 202, 249, 0.2)",
            zIndex: 1,
          }}
        />

        <ButtonBase
          onClick={() => handleToggle(false)}
          disabled={disabled}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 14px",
            borderRadius: "16px",
            zIndex: 2,
            color: !isLandline ? "#1565C0" : "#637381",
            fontWeight: !isLandline ? 600 : 500,
            fontSize: "0.75rem",
          }}
        >
          <SmartphoneIcon sx={{ fontSize: "1rem" }} />
          Mobile
        </ButtonBase>

        <ButtonBase
          onClick={() => handleToggle(true)}
          disabled={disabled}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 14px",
            borderRadius: "16px",
            zIndex: 2,
            color: isLandline ? "#1565C0" : "#637381",
            fontWeight: isLandline ? 600 : 500,
            fontSize: "0.75rem",
          }}
        >
          <PhoneIcon sx={{ fontSize: "1rem" }} />
          Landline
        </ButtonBase>
      </Box>

      {/* Input Fields Content */}
      <Box sx={{ flexGrow: 1 }}>
        <Controller
          name={id}
          control={control}
          defaultValue={defaultValue || ""}
          rules={{
            validate: (value) => (isLandline ? validateLandline(value) : validateMobile(value)),
          }}
          render={({ field, fieldState: { error } }) => {
            const hasError = Boolean(error?.message || errorMessage);

            const currentStringValue = typeof field.value === "string" ? field.value : "";
            const [currentStd, currentNum] = isLandline ? currentStringValue.split("-") : ["", ""];

            return (
              <Box>
                {!isLandline ? (
                  /* --- MOBILE MODE --- */
                  <TextField
                    sx={inputStyles.select}
                    fullWidth
                    id={`${id}-mobile`}
                    label={`${label}${required ? "*" : ""}`}
                    value={currentStringValue.replace("-", "")}
                    disabled={disabled}
                    error={hasError}
                    variant="outlined"
                    autoComplete="off"
                    placeholder="Enter 10-digit mobile number"
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, "").slice(0, 10);
                      field.onChange(numericValue);
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+91 |</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric" }}
                  />
                ) : (
                  /* --- LANDLINE MODE --- */
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: hasError ? "1px solid #d32f2f" : "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "4px 12px",
                      backgroundColor: disabled ? "#f5f5f5" : "#fff",
                      height: "56px",
                      position: "relative",
                      transition: "border-color 0.2s",
                      "&:focus-within": {
                        borderColor: hasError ? "#d32f2f" : "#1976d2",
                        borderWidth: "2px",
                        padding: "3px 11px",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        top: "-9px",
                        left: "10px",
                        backgroundColor: "#fff",
                        padding: "0 4px",
                        fontSize: "0.75rem",
                        color: hasError ? "#d32f2f" : "text.secondary",
                        zIndex: 1,
                      }}
                    >
                      {label} (Landline){required ? "*" : ""}
                    </Box>

                    {/* STD Field Area */}
                    <input
                      type="text"
                      placeholder="STD"
                      disabled={disabled}
                      value={currentStd || ""}
                      inputMode="numeric"
                      onChange={(e) => {
                        /* --- UPDATED TO SLICE TO 8 DIGITS --- */
                        const stdVal = e.target.value.replace(/\D/g, "").slice(0, 8);
                        field.onChange(stdVal || currentNum ? `${stdVal}-${currentNum || ""}` : "");
                      }}
                      style={{
                        // Scales dynamically up to 8 characters wide seamlessly
                        width: currentStd ? `${currentStd.length}ch` : "4ch",
                        minWidth: "30px",
                        border: "none",
                        outline: "none",
                        fontSize: "1rem",
                        fontFamily: "inherit",
                        background: "transparent",
                        flexGrow: 0,
                        boxSizing: "content-box",
                      }}
                    />

                    <Box sx={{ mx: 1, color: "text.secondary", userSelect: "none" }}>-</Box>

                    {/* Number Field Area */}
                    <input
                      type="text"
                      placeholder="Enter Landline Number"
                      disabled={disabled}
                      value={currentNum || ""}
                      inputMode="numeric"
                      onChange={(e) => {
                        /* --- UPDATED TO SLICE TO 11 DIGITS --- */
                        const numVal = e.target.value.replace(/\D/g, "").slice(0, 11);
                        field.onChange(currentStd || numVal ? `${currentStd || ""}-${numVal}` : "");
                      }}
                      style={{
                        flexGrow: 1,
                        border: "none",
                        outline: "none",
                        fontSize: "1rem",
                        fontFamily: "inherit",
                        background: "transparent",
                        width: "0",
                      }}
                    />
                  </Box>
                )}

                {hasError && (
                  <FormHelperText error sx={{ ml: 2, mt: "4px" }}>
                    {error?.message || errorMessage}
                  </FormHelperText>
                )}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export const EmailInputForm: React.FC<InputProps> = ({
  label,
  id,
  type = "text",
  control,
  errorMessage,
  placeholder = `Enter the ${label}`,
  defaultValue,
  disabled = false,
  required = true,
  customErrorMessage = `${label} is required`,
  maxLength = 200,
}) => {
  return (
    <>
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue || ""}
        rules={{
          required: required ? customErrorMessage : false,
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address.",
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            sx={inputStyles.select}
            fullWidth
            id={id}
            label={label}
            {...field}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error?.message) || Boolean(errorMessage)}
            helperText={error?.message}
            variant="outlined"
            className="my-2"
            autoComplete="off"
            inputProps={{
              maxLength: maxLength,
            }}
          />
        )}
      />
    </>
  );
};

export const AmountInputForm: React.FC<InputProps> = ({
  label,
  id,
  type = "number",
  control,
  errorMessage,
  placeholder = `Enter the ${label}`,
  defaultValue,
  disabled = false,
  prefix = true,
  prefixText = "₹ ",
  required = true,
  customErrorMessage = `${label} is required`,
  maxLength = 200,
}) => {
  return (
    <>
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue || ""}
        rules={{
          required: required ? customErrorMessage : false,
          pattern: {
            value: required ? /^(?!0(\.0+)?$)\d+(\.\d+)?$/ : /^\d+$/,
            message: "Please enter a valid number.",
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            sx={inputStyles.select}
            fullWidth
            id={id}
            label={label}
            {...field}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error?.message) || Boolean(errorMessage)}
            helperText={error?.message}
            variant="outlined"
            className="my-2"
            autoComplete="off"
            InputProps={{
              startAdornment: prefix && (
                <InputAdornment position="start">{prefixText}</InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: maxLength,
            }}
          />
        )}
      />
    </>
  );
};

export const PasswordInput: React.FC<InputProps> = ({
  label,
  id,
  type = "password",
  control,
  errorMessage,
  placeholder = `Enter the ${label}`,
  defaultValue,
  disabled = false,
}) => {
  const [showPassword, setShowPassword] = React.useState(true);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <>
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue || ""}
        rules={{
          required: `${label} is required`,
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <FormControl
              fullWidth
              variant="outlined"
              error={Boolean(error?.message)}
            >
              <InputLabel htmlFor={id}>{label}</InputLabel>
              <OutlinedInput
                id={id}
                {...field}
                label={label}
                type={showPassword ? "password" : "text"}
                placeholder={placeholder}
                disabled={disabled}
                error={Boolean(error?.message) || Boolean(errorMessage)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onMouseUp={(event) => {
                        event.preventDefault();
                      }}
                      edge="end"
                      sx={{ color: "#534FA1" }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                autoComplete="off"
              />
            </FormControl>
            {error?.message &&
              error?.message !== "password regex should match" && (
                <div className="text-red">
                  <span className="text-sm ps-2">{error?.message}</span>
                </div>
              )}
          </>
        )}
      />
    </>
  );
};
